import React, { useState, useContext, Fragment, useRef, forwardRef, useImperativeHandle, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { SettingsContext } from '../../context/settings-context'
import { GuideContext } from '../../context/guide-context'
import { gql, useMutation } from '@apollo/client'
import { USER_PAYMENT_FIELDS } from '../../graphql/user-fragments'
import { Form, Spinner, Alert } from 'react-bootstrap'
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js'
import ShowErrors from '../partials/ShowErrors'
import CreditCardDisplay from './CreditCardDisplay'
import { Button } from '@/components/ui/button'
import { EditIcon } from '../icons/new'

const UPDATE_PAYMENT_METHOD = gql`
    ${USER_PAYMENT_FIELDS}
    mutation UpdatePaymentMethod($paymentMethodInput: PaymentMethodInput) {
        updatePaymentMethod(paymentMethodInput: $paymentMethodInput) {
            ...UserPaymentFields
        }
    }
`

const PaymentDetails = forwardRef(({ userData, data }, ref) => {
    const elements = useElements()
    const stripe = useStripe()
    const navigate = useNavigate()

    const cardElementRef = useRef(null)
    const { theme } = useContext(SettingsContext)
    const { guide } = useContext(GuideContext)

    const [edit, setEdit] = useState(false)
    const [errors, setError] = useState([])
    const [processing, setProcessing] = useState(false)
    const [paymentData, setPaymentData] = useState(data)
    const [cardElementKey, setCardElementKey] = useState(0)

    useImperativeHandle(ref, () => ({
        focusCardNumber: () => {
            if (cardElementRef.current) {
                cardElementRef.current.focus()
            }
        },
    }))

    useEffect(() => {
        if (edit) {
            setCardElementKey((prev) => prev + 1)
            setError([])
        }
    }, [edit])

    const cardElementOptions = {
        hidePostalCode: true,
        style: {
            base: {
                fontSize: '16px',
                fontFamily: 'Poppins',
                color: theme === 'dark' ? '#FFFFFF' : '#8A92A6',
                '::placeholder': {
                    color: '#aab7c4',
                },
            },
            invalid: {
                color: '#c03221',
            },
        },
    }

    const getBillingDetails = () => ({
        name: `${userData.firstName} ${userData.lastName}`,
        address: {
            city: userData.city,
            country: userData.country,
            line1: userData.addressLine1,
            line2: userData.addressLine2,
            postal_code: userData.postalCode,
            state: userData.state,
        },
        email: userData.email,
        phone: userData.phone,
    })

    const submitCardDetails = async (event) => {
        event.preventDefault()

        if (!stripe || !elements) {
            setError([{ message: 'Stripe is not initialized. Please try again.' }])
            return
        }

        setProcessing(true)
        setError([])

        try {
            const cardElement = elements.getElement(CardElement)

            const { error, paymentMethod } = await stripe.createPaymentMethod({
                type: 'card',
                card: cardElement,
                billing_details: getBillingDetails(),
            })

            if (error) {
                setError([error])
                setProcessing(false)
                return
            }

            // Process the payment method
            const cardData = {
                cardBrand: paymentMethod.card.brand,
                cardExpMonth: paymentMethod.card.exp_month,
                cardExpYear: paymentMethod.card.exp_year,
                cardLastDigits: paymentMethod.card.last4,
                id: paymentMethod.id,
            }

            await updatePaymentMethod({ variables: { paymentMethodInput: cardData } })
        } catch (err) {
            setError([{ message: 'An unexpected error occurred. Please try again.' }])
            setProcessing(false)
        }
    }

    const handleCancelEdit = () => {
        setEdit(false)
        setError([])
    }

    const [updatePaymentMethod] = useMutation(UPDATE_PAYMENT_METHOD, {
        onError({ graphQLErrors }) {
            setError(graphQLErrors)
            setProcessing(false)
        },
        refetchQueries: ['Users'],
        onCompleted: (response) => {
            setProcessing(false)
            setError([])
            setPaymentData(response.updatePaymentMethod)
            setEdit(false)

            setTimeout(() => {
                if (data?.campaignsAggregate?.count === 0 && guide === 'on') {
                    navigate('/campaigns/new')
                }
            }, 1000)
        },
    })

    return (
        <div className="payment-details">
            <h5 className="mb-3 text-white text-lg">Payment Details</h5>
            <div className="payment-details-body">
                {/* Display existing payment method */}
                {paymentData.paymentMethodId && <CreditCardDisplay paymentData={paymentData} />}

                {!data.country && (
                    <Alert
                        variant="info alert-left alert-dismissible fade show d-flex align-items-center"
                        role="alert"
                        dismissible
                        style={{ marginBottom: '20px' }}
                    >
                        <div>To add payment details, please enter your valid address and click Update button.</div>
                    </Alert>
                )}

                {edit ? (
                    <Form onSubmit={submitCardDetails}>
                        <Form.Group className="col-md-12 form-group">
                            <Form.Label className="">Card Details</Form.Label>
                            <div className="form-control stripe-card-element-wrapper">
                                <CardElement
                                    key={cardElementKey}
                                    options={cardElementOptions}
                                    onReady={(el) => {
                                        cardElementRef.current = el
                                        console.log('CardElement mounted and ready', {
                                            stripe: !!stripe,
                                            elements: !!elements,
                                        })
                                    }}
                                />
                            </div>
                        </Form.Group>

                        {errors.length > 0 && (
                            <div className="pb-3">
                                <ShowErrors errors={errors} />
                            </div>
                        )}

                        <div className="d-flex gap-2">
                            <Button type="submit" disabled={processing || !stripe}>
                                {processing ? (
                                    <>
                                        <Spinner
                                            as="span"
                                            animation="grow"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                        />{' '}
                                        Updating...
                                    </>
                                ) : (
                                    'Update'
                                )}
                            </Button>
                            <Button
                                type="button"
                                variant="destructive"
                                disabled={processing}
                                onClick={handleCancelEdit}
                            >
                                Cancel
                            </Button>
                        </div>
                    </Form>
                ) : (
                    <Button
                        type="button"
                        variant="secondary"
                        className="step-4"
                        onClick={() => setEdit(true)}
                        disabled={!data.country}
                    >
                        <EditIcon size={18} />
                        {paymentData.paymentMethodId ? 'Edit' : 'Add'}
                    </Button>
                )}
            </div>
        </div>
    )
})

PaymentDetails.displayName = 'PaymentDetails'
export default PaymentDetails
